import { ConfigProvider, Select } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs, ManipulateType } from 'dayjs';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import StyledRangePicker from '@/components/DatePicker/StyledRangePicker';
import { MainText } from '@/components/Typography/NamedTypo';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import getDatePreset, {
  getDateRangeByDateT,
  RangeUnit,
  rangeUnitMap,
} from '@/utils/getDatePreset';
import {
  DATE_FORMAT,
  getDateRangeWithEndDateOffset,
  getEndDateManipulatedDateRange,
  getTimezoneDayjs,
} from '@/utils/time';

const { Option } = Select;

type Props = {
  dateState: DateFilterState;
  setDateState: (state: DateFilterState) => void;
  reverse?: boolean;
  hideRange?: boolean;
  size?: SizeType;
};

export type DateFilterState = {
  dateRange: [Dayjs, Dayjs];
  rangeUnit: RangeUnit;
  presetItem: number | null;
};

const DatePresetFilter = (props: Props) => {
  const color = useColor();
  const { rangeUnit, presetItem, dateRange } = props.dateState;
  const onPresetItemClick = (value: number) => {
    const range = getDateRangeByDateT(rangeUnit, value);
    props.setDateState({
      rangeUnit,
      dateRange: range,
      presetItem: value,
    });
  };

  const presetOptions = useMemo(() => {
    return getDatePreset(rangeUnit, true).map((preset) => {
      return {
        label: <Flex padding="0px 4px">{preset.name}</Flex>,
        value: preset.value,
      };
    });
  }, [rangeUnit]);

  const pickerType = useMemo(() => {
    return rangeUnitMap[rangeUnit] as RangePickerProps['picker'];
  }, [rangeUnit]);

  const onRangeUnitSelect = (value: RangeUnit) => {
    let presetValue = value === RangeUnit.Daily ? 2 : 3;
    const dateRange = getDateRangeByDateT(value, presetValue);

    props.setDateState({
      dateRange,
      rangeUnit: value,
      presetItem: presetValue,
    });
  };

  const currentValue = getDateRangeWithEndDateOffset(dateRange);

  const onChange = (dates: RangePickerProps['value'] | null) => {
    const opType = rangeUnitMap[rangeUnit] as ManipulateType;
    const newDateRange = getEndDateManipulatedDateRange(
      dates,
      dateRange,
      opType,
    );

    props.setDateState({
      rangeUnit,
      presetItem: null,
      dateRange: newDateRange,
    });
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Select: {
            borderRadius: 24,
            borderRadiusXS: 24,
            borderRadiusSM: 24,
            borderRadiusLG: 24,
          },
          DatePicker: {
            borderRadius: 24,
            borderRadiusXS: 24,
            borderRadiusSM: 24,
            borderRadiusLG: 24,
          },
        },
      }}
    >
      <Style
        a="center"
        g={6}
        j={props.reverse ? 'flex-start' : 'flex-end'}
        w="wrap"
        style={{ rowGap: 12, width: 'fit-content' }}
        d={props.reverse ? 'row-reverse' : 'row'}
      >
        {!props.hideRange && dateRange && (
          <MainText color={color.gray600}>
            {dateRange[0].format(DATE_FORMAT.readable)} -{' '}
            {dateRange[1]
              .subtractVal(1, 'day')
              .format(DATE_FORMAT.readable)}{' '}
          </MainText>
        )}

        <Flex g={6}>
          <Select
            size={props.size || 'small'}
            onSelect={onRangeUnitSelect}
            value={rangeUnit}
            style={{ width: 110 }}
          >
            <Option value={RangeUnit.Daily}>Daily</Option>
            <Option value={RangeUnit.Weekly}>Weekly</Option>
            <Option value={RangeUnit.Monthly}>Monthly</Option>
          </Select>
          <Select
            size={props.size || 'small'}
            onSelect={onPresetItemClick}
            value={presetItem ?? undefined}
            placeholder="Custom"
            style={{ width: 120 }}
            options={presetOptions}
          />
        </Flex>
        <StyledRangePicker
          allowEmpty
          size={props.size || 'small'}
          picker={pickerType}
          value={currentValue}
          onChange={onChange}
        />
      </Style>
    </ConfigProvider>
  );
};

const Style = styled(Flex)``;

const useDatePresetFilter = ({
  key,
  initialValue,
}: {
  key: string;
  initialValue?: DateFilterState;
}) => {
  const defaultDateState: DateFilterState = useMemo(
    () =>
      initialValue || {
        dateRange: [
          getTimezoneDayjs().startOf('day').addVal(-30, 'day'),
          getTimezoneDayjs().startOf('day'),
        ],
        presetItem: 3,
        rangeUnit: RangeUnit.Monthly,
      },
    [initialValue],
  );

  // presetItem을 useWorkspaceStorage로 관리
  const [dateState, setDateState] = useWorkspaceStorage<DateFilterState>(
    key,
    defaultDateState,
    {
      parser: (values: string) => {
        try {
          const data = JSON.parse(values);
          return {
            dateRange: [
              getTimezoneDayjs(data.dateRange[0]),
              getTimezoneDayjs(data.dateRange[1]),
            ],
            presetItem: data.presetItem,
            rangeUnit: data.rangeUnit,
          };
        } catch {
          return undefined;
        }
      },
      serializer: (value) => {
        return JSON.stringify({
          dateRange: [value.dateRange[0].toJSON(), value.dateRange[1].toJSON()],
          presetItem: value.presetItem,
          rangeUnit: value.rangeUnit,
        });
      },
    },
  );

  // dateRange 계산: presetItem이 있으면 계산, 없으면 storage에서 읽은 값 사용
  const computedDateState = useMemo<DateFilterState>(() => {
    if (dateState.presetItem !== null && dateState.presetItem !== undefined) {
      return {
        dateRange: getDateRangeByDateT(
          dateState.rangeUnit,
          dateState.presetItem,
        ),
        presetItem: dateState.presetItem,
        rangeUnit: dateState.rangeUnit,
      };
    }
    // presetItem이 null일 때 storage에서 읽은 값 사용
    if (dateState.dateRange) {
      return dateState;
    }
    return defaultDateState;
  }, [dateState, defaultDateState]);

  // 동기적으로 처리되므로 항상 초기화 완료
  return {
    dateState: computedDateState,
    onDateFilterChange: setDateState,
  };
};

export default React.memo(DatePresetFilter);
export { useDatePresetFilter };
